import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fmtDate, fmtMoney } from "@/components/DataTable";
import { badgeClass } from "@/lib/config";
import {
  Inbox, AlertOctagon, Timer, Clock, ListTodo, ShieldCheck, Ban, PhoneCall,
  Flame, FileWarning, TrendingDown, Anchor,
} from "lucide-react";

const CARDS = [
  { key: "unassigned_work", label: "Unassigned Work", icon: Inbox, tone: "amber" },
  { key: "urgent_cases", label: "Urgent Cases", icon: AlertOctagon, tone: "red" },
  { key: "sla_at_risk", label: "SLA At Risk", icon: Timer, tone: "amber" },
  { key: "overdue_promises", label: "Overdue Promises", icon: Clock, tone: "red" },
  { key: "overdue_tasks", label: "Overdue Tasks", icon: ListTodo, tone: "amber" },
  { key: "certificates", label: "Certificate Requests", icon: ShieldCheck, tone: "blue" },
  { key: "cancellation_nonrenewal", label: "Cancellation / Nonrenewal", icon: Ban, tone: "red" },
  { key: "renewals_requiring_contact", label: "Renewals Requiring Contact", icon: PhoneCall, tone: "amber" },
  { key: "high_severity_claims", label: "High-Severity Claims", icon: Flame, tone: "red" },
  { key: "import_exceptions", label: "Import Exceptions", icon: FileWarning, tone: "amber" },
  { key: "stale_opportunities", label: "Stale Opportunities", icon: TrendingDown, tone: "blue" },
];

const toneCls = {
  red: "border-l-seared", amber: "border-l-seaamber", blue: "border-l-atlantic",
};

const SNAP = [
  { key: "active_clients", label: "Active Clients" },
  { key: "households", label: "Households" },
  { key: "commercial_accounts", label: "Commercial Accounts" },
  { key: "active_policies", label: "Active Policies" },
  { key: "annual_premium", label: "Annual Premium", money: true },
  { key: "open_opportunities", label: "Open Opportunities" },
  { key: "pipeline", label: "Pipeline", money: true },
  { key: "upcoming_renewals", label: "Upcoming Renewals (90d)" },
  { key: "open_claims", label: "Open Claims" },
  { key: "retention", label: "Retention", pct: true },
  { key: "data_quality", label: "Data Quality", pct: true },
];

export default function CommandCenter() {
  const [cards, setCards] = useState({});
  const [snap, setSnap] = useState({});
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState([]);
  const [commTile, setCommTile] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [cc, bs] = await Promise.all([
          api.get("/dashboard/command-center"), api.get("/dashboard/book-snapshot"),
        ]);
        setCards(cc.data.cards); setSnap(bs.data.snapshot);
      } catch {}
      try { const { data } = await api.get("/commissions/dashboard-tile"); setCommTile(data); } catch {}
    })();
  }, []);

  const openDrill = async (card) => {
    setDrill(card); setDrillRows([]);
    try { const { data } = await api.get(`/dashboard/card/${card.key}`); setDrillRows(data.items); } catch {}
  };

  const rowLabel = (r) => r.subject || r.title || r.certificate_holder || r.carrier_claim_number || r.name || r.policy_number || r.batch_ref || r.id;
  const rowSub = (r) => [r.case_number, r.priority, r.status, r.severity, r.carrier_name, r.line, r.outcome, r.contact_status]
    .filter(Boolean).slice(0, 3).join(" · ");

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Service Command Center" subtitle="What needs attention today." />

      {commTile && (commTile.this_month !== 0 || commTile.ytd !== 0 || commTile.count > 0) && (
        <button data-testid="commission-tile" onClick={() => nav("/commissions")}
          className="w-full text-left bg-navy text-white rounded-sm p-4 mb-4 flex flex-wrap items-center gap-6 hover:bg-atlantic nav-link">
          <div><div className="text-xs uppercase tracking-wide text-sky/80">Commission Earned This Month</div>
            <div className="font-serif text-3xl mt-0.5">{(commTile.this_month < 0 ? "-$" : "$") + Math.abs(commTile.this_month).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
          <div className="border-l border-white/20 pl-6"><div className="text-xs uppercase tracking-wide text-sky/80">Year to Date</div>
            <div className="font-serif text-2xl mt-0.5">{(commTile.ytd < 0 ? "-$" : "$") + Math.abs(commTile.ytd).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
          <div className="border-l border-white/20 pl-6"><div className="text-xs uppercase tracking-wide text-sky/80">Unpaid</div>
            <div className="font-serif text-2xl mt-0.5">${Math.abs(commTile.unpaid).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </button>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {CARDS.map((c) => {
          const n = cards[c.key] ?? 0;
          return (
            <button key={c.key} data-testid={`cc-card-${c.key}`} onClick={() => openDrill(c)}
              className={`text-left bg-white border border-border border-l-4 ${toneCls[c.tone]} rounded-sm p-4 hover:bg-ice nav-link`}>
              <div className="flex items-start justify-between">
                <c.icon className={`h-5 w-5 ${c.tone === "red" ? "text-seared" : c.tone === "amber" ? "text-seaamber" : "text-atlantic"}`} />
                <span className="font-serif text-3xl text-navy leading-none" data-testid={`cc-count-${c.key}`}>{n}</span>
              </div>
              <div className="text-xs text-slate mt-3 font-medium">{c.label}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl text-navy mb-3 flex items-center gap-2"><Anchor className="h-5 w-5 text-atlantic" /> Book Snapshot</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
          {SNAP.map((s) => (
            <div key={s.key} className="bg-white border border-border rounded-sm p-4" data-testid={`snap-${s.key}`}>
              <div className="font-serif text-2xl text-navy">
                {s.money ? fmtMoney(snap[s.key]) : s.pct ? (snap[s.key] == null ? "n/a" : `${snap[s.key]}%`) : (snap[s.key] ?? 0)}
              </div>
              <div className="text-xs text-mutedsea mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto end-scroll">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">{drill?.label}</DialogTitle></DialogHeader>
          <div className="divide-y divide-border">
            {drillRows.length === 0 ? <p className="text-sm text-mutedsea py-4">No records.</p> :
              drillRows.map((r) => (
                <button key={r.id} data-testid={`drill-row-${r.id}`}
                  onClick={() => { if (r.party_id) { setDrill(null); nav(`/clients/${r.party_id}`); } }}
                  className="w-full text-left py-2.5 flex items-center justify-between hover:bg-ice px-2 nav-link">
                  <div>
                    <div className="text-sm text-slate font-medium">{rowLabel(r)}</div>
                    <div className="text-xs text-mutedsea">{rowSub(r)}</div>
                  </div>
                  {r.due_date && <Badge variant="outline" className={badgeClass(r.status)}>{fmtDate(r.due_date)}</Badge>}
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
